import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Layout } from './components/layout/Layout';
import { DashboardShowcase } from './components/dashboard/DashboardShowcase';
import { LoginPage } from './components/auth/LoginPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import AppointmentReminderProvider from './components/notifications/AppointmentReminderProvider';
import NewEntryNotificationProvider from './components/notifications/NewEntryNotificationProvider';
import ErrorBoundary from './components/common/ErrorBoundary';
import NotFound from './components/common/NotFound';
import notificationsService from './services/notifications.service';
import { useRealtimePushNotifications } from './hooks/useRealtimePushNotifications';
import './styles/glassmorphism.css';
import './styles/global-scrollbar.css';
import './styles/ui-master-optimizations.css';
import './styles/responsive-layout.css';
import './styles/performance-optimizations.css';
import './styles/unified-buttons.css';
import './styles/themes/light.css';
import './App.css';

// Lazy-loaded page components for code splitting
const DocumentsPageWithTabs = lazy(() => import('./components/pages/DocumentsPageWithTabs'));
const EmailPageWithTabs = lazy(() => import('./components/pages/EmailPageWithTabs'));
const ContactsPageWithTabs = lazy(() => import('./components/pages/ContactsPageWithTabs'));
const AnalyticsPage = lazy(() => import('./components/pages/AnalyticsPage'));
const ProjectManagementPage = lazy(() => import('./pages/ProjectManagementPage'));
const SettingsPage = lazy(() => import('./components/pages/SettingsPage'));
const RecruitingPage = lazy(() => import('./pages/RecruitingPage'));
const JobOrdersPage = lazy(() => import('./components/jobOrders/JobOrdersPage'));
const HRDashboard = lazy(() => import('./components/pages/HRDashboard'));
const SuperAdminDashboard = lazy(() => import('./components/pages/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));
const JobsPage = lazy(() => import('./components/pages/JobsPage').then(m => ({ default: m.JobsPage })));
const NewJobPosting = lazy(() => import('./components/pages/NewJobPosting').then(m => ({ default: m.NewJobPosting })));


// NEW: Modern UI Components with Animations
const CalendarPageNew = lazy(() => import('./pages/CalendarPageNew'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const EmailPageModern = lazy(() => import('./components/pages/EmailPageModern'));

// Realtime Push Notifications Provider - connects Supabase Realtime to Browser Push Notifications
const RealtimePushNotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  // Enable push notifications when user is authenticated
  useRealtimePushNotifications({
    companyId: user?.company_id,
    enabled: !!user,
    tables: {
      candidates: true,
      tasks: true,
      appointments: true,
      activities: true,
    },
  });

  return <>{children}</>;
};

// Loading fallback component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '400px'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid var(--glass-border)',
      borderTopColor: 'var(--accent-primary)',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Start notification service on app mount
  useEffect(() => {
    console.log('🚀 Initializing notification service...');
    notificationsService.startPolling();

    // Cleanup on unmount
    return () => {
      notificationsService.stopPolling();
    };
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <LayoutProvider>
                <AppointmentReminderProvider>
                  <Router>
                    <RealtimePushNotificationsProvider>
                    <NewEntryNotificationProvider>
                    <div className="App">
              {/* Animated Gradient Background */}
              <div className="gradient-bg" />

              <Routes>
                <Route path="/login" element={<LoginPage />} />

                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardShowcase />} />

                  {/* Lazy-loaded routes with Suspense for code splitting */}
                  <Route path="documents" element={<Suspense fallback={<PageLoader />}><DocumentsPageWithTabs /></Suspense>} />
                  {/* NEW: Modern Email UI - Use /email for modern, /email-classic for old */}
                  <Route path="email" element={<Suspense fallback={<PageLoader />}><EmailPageModern /></Suspense>} />
                  <Route path="email-classic" element={<Suspense fallback={<PageLoader />}><EmailPageWithTabs /></Suspense>} />
                  {/* NEW: Modern Calendar UI - Use /calendar for efficient new version */}
                  <Route path="calendar-tasks" element={<Suspense fallback={<PageLoader />}><CalendarPage /></Suspense>} />
                  <Route path="calendar" element={<Suspense fallback={<PageLoader />}><CalendarPage /></Suspense>} />
                  {/* Legacy-Kalender entfernt — ein einziger, konsistenter Kalender */}
                  <Route path="calendar-v2" element={<Navigate to="/calendar-tasks" replace />} />
                  {/* Alte Ansicht eine Version lang als Rückfall, danach löschen (Spec 2026-10-09) */}
                  <Route path="calendar-classic" element={<Suspense fallback={<PageLoader />}><CalendarPageNew /></Suspense>} />
                  <Route path="companies" element={<Suspense fallback={<PageLoader />}><ContactsPageWithTabs /></Suspense>} />
                  <Route path="contacts" element={<Suspense fallback={<PageLoader />}><ContactsPageWithTabs /></Suspense>} />
                  <Route path="recruiting" element={<Suspense fallback={<PageLoader />}><RecruitingPage /></Suspense>} />
                  {/* Es gibt nur noch ein Dashboard — die Kennzahlenreihe des
                      Recruiting-Dashboards ist in /dashboard aufgegangen. */}
                  <Route path="recruiter-dashboard" element={<Navigate to="/dashboard" replace />} />
                  <Route path="job-orders" element={<Suspense fallback={<PageLoader />}><JobOrdersPage /></Suspense>} />
                  <Route path="jobs" element={<Suspense fallback={<PageLoader />}><JobsPage /></Suspense>} />
                  <Route path="jobs/new" element={<Suspense fallback={<PageLoader />}><NewJobPosting /></Suspense>} />
                  <Route path="positions" element={<Navigate to="/jobs" replace />} />
                  <Route path="employees" element={<Suspense fallback={<PageLoader />}><HRDashboard /></Suspense>} />
                  <Route path="super-admin" element={<Suspense fallback={<PageLoader />}><SuperAdminDashboard /></Suspense>} />
                  <Route path="analytics" element={<Suspense fallback={<PageLoader />}><AnalyticsPage /></Suspense>} />
                  <Route path="settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
                  <Route path="project-management" element={<Suspense fallback={<PageLoader />}><ProjectManagementPage /></Suspense>} />
                  {/* Integrationen sind ein Settings-Bereich */}
                  <Route path="settings/integrations" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
                  {/* Ehrliches 404 statt stillem Dashboard-Redirect (versteckte kaputte Links) */}
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>

              {/* Toast Notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  className: 'glass-toast',
                  style: {
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  },
                }}
              />
                    </div>
                  </NewEntryNotificationProvider>
                    </RealtimePushNotificationsProvider>
                  </Router>
                </AppointmentReminderProvider>
              </LayoutProvider>
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;